import { logAmplitudeEventAllowCustomers } from 'amplitude/amplitude'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, TextField, Typography, styled } from 'opensolar-ui'
import { useEffect, useState } from 'react'

const OTHER_VALUE = '__other__'

type PropTypes = {
  open: boolean
  question: string
  answers: string[]
  onDismiss: () => void
  onSelectAnswer?: (answer: string) => void
  translate?: (text: string) => string
}

const StyledDialogContent = styled(DialogContent)(() => ({
  padding: '8px',
}))

const AnswerList = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  margin: '8px',
}))

const OtherReasonField = styled(TextField)(() => ({
  marginTop: '12px',
  marginLeft: '32px',
  width: 'calc(100% - 32px)',
}))

const FinanceMarketplaceExitSurveyDialog = (props: PropTypes) => {
  const { open, question, answers, onDismiss, onSelectAnswer, translate = (t: string) => t } = props
  const [selected, setSelected] = useState<string>('')
  const [otherReason, setOtherReason] = useState<string>('')

  const displayAnswers = answers?.filter((a) => a !== 'Other') ?? []
  const hasOther = true

  useEffect(() => {
    logAmplitudeEventAllowCustomers('marketplace_exit_survey_shown', {})
  }, [])

  useEffect(() => {
    if (!open) {
      setSelected('')
      setOtherReason('')
    }
  }, [open])

  const handleSubmit = () => {
    logAmplitudeEventAllowCustomers('marketplace_exit_survey_answered', {
      answer: selected,
      other_reason: otherReason,
    })
    if (selected && onSelectAnswer) {
      onSelectAnswer(selected === OTHER_VALUE ? otherReason : selected)
    }
    onDismiss()
  }

  const isOtherSelected = selected === OTHER_VALUE

  return (
    <Dialog open={open} onClose={onDismiss} maxWidth="sm" fullWidth sx={{ zIndex: 2001 }}>
      <DialogTitle>{translate('Quick feedback')}</DialogTitle>
      <StyledDialogContent>
        <Typography textVariant="body2">{question}</Typography>
        {(displayAnswers.length > 0 || hasOther) && (
          <AnswerList>
            <RadioGroup
              value={selected}
              onChange={(e) => setSelected((e.target as HTMLInputElement).value)}
              name="exit-survey-answers"
            >
              {displayAnswers.map((answer) => (
                <FormControlLabel key={answer} value={answer} control={<Radio />} label={answer} />
              ))}
              <FormControlLabel value={OTHER_VALUE} control={<Radio />} label={translate('Other')} />
            </RadioGroup>
            {isOtherSelected && (
              <OtherReasonField
                label={translate('Please specify')}
                placeholder={translate('Type your reason here')}
                value={otherReason}
                onChange={(e) => setOtherReason(e.target.value)}
                variant="outlined"
                size="small"
                multiline
                minRows={2}
                autoFocus
              />
            )}
          </AnswerList>
        )}
      </StyledDialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onDismiss}>
          {translate('Dismiss')}
        </Button>
        <Button
          variant="contained"
          disabled={!selected || (isOtherSelected && !otherReason)}
          color={'primary'}
          onClick={handleSubmit}
        >
          {translate('Submit')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default FinanceMarketplaceExitSurveyDialog
